import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { submitInspiration, submitWaitlist, submitCollaborate, getSettings } from "@/lib/api";
import { Mail, MessageCircle } from "lucide-react";

const DialogsContext = createContext(null);
export const useDialogs = () => useContext(DialogsContext);

export const GlobalDialogsProvider = ({ children }) => {
  const [joinOpen, setJoinOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistInterest, setWaitlistInterest] = useState("");
  const [collabOpen, setCollabOpen] = useState(false);

  const value = {
    openJoin: () => setJoinOpen(true),
    openWaitlist: (interest = "") => {
      setWaitlistInterest(interest);
      setWaitlistOpen(true);
    },
    openCollaborate: () => setCollabOpen(true),
  };

  return (
    <DialogsContext.Provider value={value}>
      {children}
      <JoinDialog open={joinOpen} setOpen={setJoinOpen} />
      <WaitlistDialog open={waitlistOpen} setOpen={setWaitlistOpen} interest={waitlistInterest} />
      <CollaborateDialog open={collabOpen} setOpen={setCollabOpen} />
    </DialogsContext.Provider>
  );
};

function JoinDialog({ open, setOpen }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitInspiration(form);
      toast.success(res.message);
      setOpen(false);
      setForm({ name: "", email: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card" data-testid="join-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Join the YTCA movement</DialogTitle>
          <DialogDescription>
            Get daily creative inspiration and be first to hear about experiences, courses and studio events.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="join-name">Name</Label>
            <Input id="join-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="join-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="join-email">Email</Label>
            <Input id="join-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="join-email-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="join-submit-button">
            {loading ? "Joining..." : "Join YTCA"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function WaitlistDialog({ open, setOpen, interest }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitWaitlist({ ...form, interest });
      toast.success(res.message);
      setOpen(false);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card" data-testid="waitlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Join the waitlist</DialogTitle>
          <DialogDescription>
            {interest ? `Be first to know about “${interest}”.` : "Be first to know about upcoming classes, workshops and learning opportunities."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="wl-name">Name</Label>
            <Input id="wl-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="waitlist-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wl-email">Email</Label>
            <Input id="wl-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="waitlist-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wl-msg">Anything you'd like us to know? (optional)</Label>
            <Textarea id="wl-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What are you hoping for?" data-testid="waitlist-message-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="waitlist-submit-button">
            {loading ? "Submitting..." : "Join Waitlist"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CollaborateDialog({ open, setOpen }) {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({ email: "", whatsapp: "" });

  useEffect(() => {
    getSettings().then((s) => setContact(s.contact || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitCollaborate(form);
      toast.success(res.message);
      setOpen(false);
      setForm({ name: "", email: "", role: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = contact.whatsapp
    ? `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Hi YTCA, I'd love to collaborate with you.")}`
    : null;
  const mailLink = contact.email
    ? `mailto:${contact.email}?subject=${encodeURIComponent("Collaborate with YTCA")}`
    : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto" data-testid="collaborate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Collaborate with YTCA</DialogTitle>
          <DialogDescription>
            For artists, organisations, brands, educators and creative professionals. Reach out directly — the fastest way to talk to us.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <Button asChild={!!mailLink} variant="outline" disabled={!mailLink} className="rounded-full" data-testid="collaborate-email-button">
            {mailLink ? <a href={mailLink}><Mail className="h-4 w-4" /> Email Us</a> : <span><Mail className="h-4 w-4" /> Email Us</span>}
          </Button>
          <Button asChild={!!waLink} disabled={!waLink} className="rounded-full" data-testid="collaborate-whatsapp-button">
            {waLink ? <a href={waLink} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /> WhatsApp</a> : <span><MessageCircle className="h-4 w-4" /> WhatsApp</span>}
          </Button>
        </div>
        {!contact.whatsapp && <p className="text-center text-xs text-muted-foreground">WhatsApp will be enabled once YTCA's number is added.</p>}

        <div className="relative py-1 text-center text-xs text-muted-foreground"><span className="bg-card px-2">or send a message</span></div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="co-name">Name</Label>
            <Input id="co-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name / organisation" data-testid="collaborate-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="co-email">Email</Label>
            <Input id="co-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="collaborate-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="co-role">I am a... (optional)</Label>
            <Input id="co-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Artist / Brand / Educator / Organisation" data-testid="collaborate-role-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="co-msg">How would you like to collaborate?</Label>
            <Textarea id="co-msg" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your idea" data-testid="collaborate-message-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="collaborate-submit-button">
            {loading ? "Sending..." : "Send"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
